import { FC, ReactNode } from "react";

import { Box, Stack, useTheme } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import {
  buildOrgPageFooterAddressMapsUrl,
  formatOrgPageFooterAddress,
  getField,
  ORG_PAGE_SOURCE,
  RouteBuilders,
  parseFormBackgroundTheme,
  SOURCE_QUERY_PARAM_KEY,
} from "@simplyk/common";

import { OrganizationPageLayout } from "../../OrganizationPageLayout";
import { CreateYourOwnPage } from "../CreateYourOwnPage/CreateYourOwnPage";
import { OrganizationPageEvents } from "../OrganizationPageEvents/OrganizationPageEvents";
import { ContactUs } from "../OrganizationPageFooter/ContactUs/ContactUs";
import { getMissionHtml } from "../OrganizationMission/helpers/getMissionHtml";
import { OrganizationMission } from "../OrganizationMission/OrganizationMission";
import { OrganizationPageFooter } from "../OrganizationPageFooter/OrganizationPageFooter";
import { OrganizationPageHeader } from "../OrganizationPageHeader/OrganizationPageHeader";
import { OrganizationPageNewsletters } from "../OrganizationPageNewsletters/OrganizationPageNewsletters";
import { OrganizationPageSupport } from "../OrganizationPageSupport/OrganizationPageSupport";

import { ThemedPoweredByZeffy } from "@/canvas/layouts/ThemedBranding/ThemedPoweredByZeffy";
import { ThemedZeffyLogo } from "@/canvas/layouts/ThemedBranding/ThemedZeffyLogo";
import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { appendQueryParams } from "@/common/helpers/url";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useDynamicThemeAwareVariables } from "@/common/hooks/useDynamicThemeAwareVariables";
import { useThemedFormZeffyLogoUrl } from "@/common/hooks/useThemedFormZeffyLogoUrl";
import { useTranslate } from "@/common/hooks/useTranslate";
import { removeSearchParams, useUrlBuilder } from "@/common/hooks/useUrlBuilder";
import { OrganizationPageOutput } from "@/common/types/trpc";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import Image from "@/components/Image/Image";
import { FormButton, InnerFormButtonProps } from "@/forms-design-system/FormButton";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { ExternalLink } from "@/icons/outlined";

type OrganizationPageContentProps = {
  organizationPage: OrganizationPageOutput;
  slots?: {
    ownerView?: ReactNode;
  };
  /** If true, buttons won't have href/onClick and overlay is disabled */
  isPreview?: boolean;
  /** Controls overlay visibility when not in preview mode */
  isPublished?: boolean;
  isContactDialogOpenInPreview?: boolean;
};

/**
 * Shared content component for OrganizationPage and OrganizationPagePreview.
 * Contains the common layout and rendering logic.
 */
export const OrganizationPageContent: FC<OrganizationPageContentProps> = ({
  organizationPage,
  slots,
  isPreview = false,
  isPublished = true,
  isContactDialogOpenInPreview = false,
}) => {
  const { locale, isoLocale } = useLocaleContext();
  const theme = useTheme();
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();
  const urlBuilder = useUrlBuilder();

  const { backgroundColor } = useDynamicThemeAwareVariables(({ formsPalette, mode }) => ({
    backgroundColor: mode === "light" ? formsPalette.surface.supershy : formsPalette.surface.intense,
  }));

  const field = getField(organizationPage.organizationPageFields, locale);
  const bannerUrl = organizationPage.bannerUrl;
  const bannerAltText = organizationPage.bannerAltText;
  const logoUrl = organizationPage.organization.logoUrl;
  const organizationName = organizationPage.organization.name;
  const missionHtml = getMissionHtml(field?.description);
  const websiteUrl = organizationPage.isWebsiteShown ? (organizationPage.organization.website ?? null) : null;
  const footerBlocks = organizationPage.footerBlocks.flatMap((footerBlock) => {
    const footerBlockField =
      footerBlock.fields.find((blockField) => blockField.locale === locale) ?? footerBlock.fields[0];
    return footerBlockField
      ? [
          {
            id: footerBlock.id,
            title: footerBlockField.title,
            content: stripRichTextPrefix(footerBlockField.content) ?? "",
          },
        ]
      : [];
  });

  const MAX_BANNER_WIDTH = 976;
  const MAX_BANNER_HEIGHT = 240;

  const newsletterFormPath = organizationPage.newsletterForm
    ? urlBuilder({
        route: RouteBuilders.buildNewsletterFormLink({
          isoLocale,
          path: organizationPage.newsletterForm.path,
        }),
        searchParams: (searchParams) => ({
          ...removeSearchParams("newsletterFormId")(searchParams),
          [SOURCE_QUERY_PARAM_KEY]: ORG_PAGE_SOURCE,
        }),
      })
    : undefined;

  const renderActionButtons = ({
    size,
    endIcon,
  }: {
    size: InnerFormButtonProps["size"];
    endIcon?: InnerFormButtonProps["endIcon"];
  }) => {
    const donationCampaignUrl = organizationPage.defaultDonationCampaign?.url;
    if (!donationCampaignUrl && !newsletterFormPath) {
      return undefined;
    }

    return (
      <>
        {newsletterFormPath && (
          <FormButton
            size={size}
            variant="outline"
            color="primary"
            href={isPreview ? undefined : newsletterFormPath}
            target={isPreview ? undefined : "_blank"}
            onClick={
              isPreview
                ? undefined
                : () => {
                    logAmplitudeEvent(AmplitudeEvents.OrgPageSubscriptionStarted);
                  }
            }
            endIcon={endIcon}
          >
            {t("common", "Subscribe")}
          </FormButton>
        )}
        {donationCampaignUrl && (
          <FormButton
            size={size}
            variant="solid"
            color="primary"
            href={
              isPreview
                ? undefined
                : appendQueryParams(donationCampaignUrl, {
                    [SOURCE_QUERY_PARAM_KEY]: ORG_PAGE_SOURCE,
                  })
            }
            target={isPreview ? undefined : "_blank"}
            onClick={
              isPreview
                ? undefined
                : () => {
                    logAmplitudeEvent(AmplitudeEvents.OrgPageDonationStarted);
                  }
            }
            endIcon={endIcon}
          >
            {t("common", "Donate")}
          </FormButton>
        )}
      </>
    );
  };

  const organizationNameSlot =
    websiteUrl && !isPreview ? (
      <Box
        component="a"
        href={websiteUrl}
        target="_blank"
        rel="noopener"
        onClick={() => logAmplitudeEvent(AmplitudeEvents.OrgPageWebsiteOpened)}
        sx={(themeArg: Theme) => ({
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          textDecoration: "none",
          maxWidth: "100%",
          "&:hover": {
            textDecoration: { xs: "none", sm: "underline" },
            textDecorationColor: themeArg.palette.forms.text.intense,
          },
        })}
      >
        {organizationName}
        <FormIcon
          sx={(themeArg: Theme) => ({
            flexShrink: 0,
            color: { xs: themeArg.palette.forms.text.supershy, sm: themeArg.palette.forms.text.quiet },
          })}
        >
          <ExternalLink />
        </FormIcon>
      </Box>
    ) : (
      <>{organizationName}</>
    );

  const header = (
    <OrganizationPageHeader
      backgroundColor={backgroundColor}
      logoUrl={logoUrl}
      hasBanner={!!bannerUrl}
      isPreview={isPreview}
      websiteUrl={websiteUrl}
      onWebsiteClick={() => logAmplitudeEvent(AmplitudeEvents.OrgPageWebsiteOpened)}
      slots={{
        organizationName: organizationNameSlot,
        mobileButtons: renderActionButtons({
          size: "medium",
          endIcon: (
            <FormIcon size="small">
              <ExternalLink />
            </FormIcon>
          ),
        }),
        desktopButtons: renderActionButtons({ size: "large" }),
      }}
    />
  );

  const footerSlot = (
    <OrganizationPageFooter
      websiteUrl={websiteUrl}
      socialLinks={organizationPage.socialLinks}
      address={
        organizationPage.footerAddress?.address.trim()
          ? {
              label: formatOrgPageFooterAddress(organizationPage.footerAddress),
              mapsUrl: buildOrgPageFooterAddressMapsUrl(organizationPage.footerAddress),
            }
          : null
      }
      footerBlocks={footerBlocks}
      contactUsButton={
        organizationPage.isContactUsEnabled ? (
          <ContactUs
            organizationPageId={organizationPage.id}
            organizationName={organizationName}
            isPreview={isPreview}
            isDialogOpenInPreview={isContactDialogOpenInPreview}
          />
        ) : undefined
      }
      registeredCharityDetails={organizationPage.organization.registeredCharityDetails}
      isPreview={isPreview}
      theme={theme}
      onWebsiteClick={() => logAmplitudeEvent(AmplitudeEvents.OrgPageWebsiteOpened)}
      onAddressClick={() => logAmplitudeEvent(AmplitudeEvents.OrgPageAddressOpened)}
      onAddressCopy={() => logAmplitudeEvent(AmplitudeEvents.OrgPageAddressCopied)}
    />
  );

  const logoHref = useThemedFormZeffyLogoUrl("organization_page");
  const zeffyLogo = <ThemedZeffyLogo href={logoHref} />;

  const hasOverlay = !isPreview && !isPublished;

  const backgroundTheme = parseFormBackgroundTheme(organizationPage.backgroundTheme);

  return (
    <OrganizationPageLayout
      hasOverlay={hasOverlay}
      backgroundTheme={backgroundTheme}
      brandColorHex={organizationPage.color}
      slots={{
        ownerView: slots?.ownerView,
        header,
        zeffyLogo,
        poweredByZeffy: (
          <Stack alignItems="center" gap={1}>
            <ThemedPoweredByZeffy link={{ href: logoHref }} slots={{ logo: <ThemedZeffyLogo /> }} />
            <CreateYourOwnPage href={logoHref} />
          </Stack>
        ),
        banner: bannerUrl ? (
          <Image
            src={bannerUrl}
            alt={bannerAltText || organizationName}
            width={MAX_BANNER_WIDTH}
            height={MAX_BANNER_HEIGHT}
            resolution={MAX_BANNER_WIDTH * 2}
            nextImage
            priority
            sx={{
              margin: "0 auto",
              aspectRatio: { xs: "343 / 93", sm: `${MAX_BANNER_WIDTH} / ${MAX_BANNER_HEIGHT}` },
              objectFit: "cover",
              borderRadius: theme.spacing(2),
              width: "100%",
              height: "auto",
            }}
          />
        ) : undefined,
        description: missionHtml ? <OrganizationMission missionHtml={missionHtml} /> : undefined,
        events:
          organizationPage.upcomingEvents.rows.length > 0 || organizationPage.pastEvents.rows.length > 0 ? (
            <OrganizationPageEvents
              organizationId={organizationPage.organizationId}
              upcomingEvents={organizationPage.upcomingEvents}
              pastEvents={organizationPage.pastEvents}
            />
          ) : null,
        otherCampaigns:
          organizationPage.supportForms.length > 0 ? (
            <OrganizationPageSupport
              supportForms={organizationPage.supportForms}
              primaryDonationCampaignId={organizationPage.defaultDonationCampaign?.id}
            />
          ) : null,
        newsletters:
          organizationPage.isNewslettersSectionEnabled && organizationPage.newsletters.length > 0 ? (
            <OrganizationPageNewsletters
              organizationId={organizationPage.organizationId}
              organizationPageUrl={organizationPage.url}
              newsletters={organizationPage.newsletters}
              isPreview={isPreview}
            />
          ) : null,
        footer: footerSlot,
      }}
    />
  );
};

const stripRichTextPrefix = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }
  return value.replace(/^LXCL:/, "");
};
